import { SOURCE_KEY } from './crmTagKey.js';

/**
 * CRM tags, as a customer chooses them — and the three fields the server still takes.
 *
 * A customer sees one concept: the CRM tags on a contact. Internally three things stay
 * distinct, because the safety rules depend on them: the protected stage group (at most
 * one, CRM record authority, downgrade protection), legacy relationship types that carry a
 * tag, and generic tags. GET /crm-tags says which each tag is (`kind`), so a picker can show
 * one list and this module can hand every existing endpoint the fields it validates.
 *
 * Free of React and of api.ts on purpose, like navigation.ts and reviewModel.ts: the
 * translation between "the tags chosen" and "the fields sent" is the part with real logic,
 * and the architecture tests import it without a DOM.
 */

export type CrmTagKind = 'stage' | 'type' | 'tag';

/** One tag a customer can choose, as GET /crm-tags lists it. */
export interface CrmTagOption {
  readonly id: string;
  readonly kind: CrmTagKind;
  readonly key: string;
  readonly label: string;
  readonly externalTagId: string | null;
  readonly group: 'commercial_stage' | null;
}

/**
 * A tag a stored selection may still name that is no longer available: removed, archived,
 * or its CRM tag gone. Only ever used for its NAME — never offered, never sent.
 */
export interface RetiredCrmTag {
  readonly id: string;
  readonly kind: CrmTagKind;
  readonly key: string;
  readonly label: string;
}

/** GET /crm-tags. Only tags that would actually reach the CRM are listed. */
export interface CrmTagVocabulary {
  readonly tags: readonly CrmTagOption[];
  /** Stage keys with no CRM tag chosen yet. */
  readonly unmappedStages: readonly string[];
  /** Tags that are no longer available, so a selection naming one can say what it was. */
  readonly retired?: readonly RetiredCrmTag[];
  /** True only for the placeholder used before GET /crm-tags has answered. */
  readonly pending?: true;
}

/**
 * The vocabulary before the server has answered. Marked, so nothing is called "no longer
 * available" merely because the list has not loaded yet.
 */
export const EMPTY_VOCABULARY: CrmTagVocabulary = { tags: [], unmappedStages: [], retired: [], pending: true };

/** The words for a tag that resolves nowhere. Never its raw key. */
export const UNAVAILABLE_TAG = 'Unavailable tag';

/** The three fields every write endpoint takes. */
export interface CrmTagFields {
  readonly commercialStage: string | null;
  readonly relationshipTypeKeys: readonly string[];
  readonly crmTagKeys: readonly string[];
}

/** Every kind: email capture and business cards. */
export const ALL_KINDS: readonly CrmTagKind[] = ['stage', 'type', 'tag'];
/** Image and file capture: intake items and their defaults take no relationship types. */
export const INTAKE_KINDS: readonly CrmTagKind[] = ['stage', 'tag'];

/**
 * `stage:Lead`, `type:rel:vendor`, `tag:VIP` — the same ids the server uses.
 *
 * Self-describing on purpose. A value the vocabulary no longer lists (a stage whose CRM tag
 * was never chosen, a type whose tag was removed) still round-trips: it is shown, it can
 * be removed, and saving does not silently drop it.
 */
export const idFor = (kind: CrmTagKind, key: string): string => `${kind}:${key}`;

export function parseId(id: string): { kind: CrmTagKind; key: string } | null {
  const at = id.indexOf(':');
  if (at <= 0) return null;
  const kind = id.slice(0, at);
  const key = id.slice(at + 1);
  if ((kind !== 'stage' && kind !== 'type' && kind !== 'tag') || key === '') return null;
  return { kind, key };
}

const kindOf = (id: string, vocab: CrmTagVocabulary): CrmTagKind | null =>
  vocab.tags.find((t) => t.id === id)?.kind ?? parseId(id)?.kind ?? null;

/**
 * The tags a picker may offer: the kinds allowed here.
 *
 * Where a contact came from is recorded by the capture itself, never as a tag, so the
 * retired source key is never offered even if a list carried it. The CRM tag that used to
 * back it is an ordinary tag once a customer adds it under CRM Tags, and is offered then.
 */
export function pickerOptions(
  vocab: CrmTagVocabulary, kinds: readonly CrmTagKind[] = ALL_KINDS,
): CrmTagOption[] {
  return vocab.tags.filter((t) => kinds.includes(t.kind) && t.key !== SOURCE_KEY);
}

/** The three fields → the chosen tag ids, stage first. */
export function toSelection(
  fields: CrmTagFields, _vocab: CrmTagVocabulary, kinds: readonly CrmTagKind[] = ALL_KINDS,
): string[] {
  const ids: string[] = [];
  if (fields.commercialStage && kinds.includes('stage')) ids.push(idFor('stage', fields.commercialStage));
  if (kinds.includes('type')) for (const k of fields.relationshipTypeKeys) ids.push(idFor('type', k));
  if (kinds.includes('tag')) for (const k of fields.crmTagKeys) ids.push(idFor('tag', k));
  return [...new Set(ids)];
}

/**
 * The chosen tag ids → the three fields.
 *
 * Throws on two stages: the group is single-valued, `toggle` never produces two, and a
 * selection that somehow holds two must never be guessed at — picking one would be a
 * silent decision about somebody's CRM record.
 */
export function fromSelection(
  ids: readonly string[], vocab: CrmTagVocabulary, kinds: readonly CrmTagKind[] = ALL_KINDS,
): { commercialStage: string | null; relationshipTypeKeys: string[]; crmTagKeys: string[] } {
  const stages: string[] = [];
  const relationshipTypeKeys: string[] = [];
  const crmTagKeys: string[] = [];
  for (const id of new Set(ids)) {
    const parsed = parseId(id);
    const kind = kindOf(id, vocab);
    if (!parsed || !kind || !kinds.includes(kind)) continue;
    if (kind === 'stage') stages.push(parsed.key);
    else if (kind === 'type') relationshipTypeKeys.push(parsed.key);
    else crmTagKeys.push(parsed.key);
  }
  if (stages.length > 1) {
    throw new Error(`Only one stage tag can be on a contact; got ${stages.join(', ')}`);
  }
  return { commercialStage: stages[0] ?? null, relationshipTypeKeys, crmTagKeys };
}

/** Adds or removes one tag. Adding a stage removes any other stage. */
export function toggle(ids: readonly string[], id: string, vocab: CrmTagVocabulary): string[] {
  if (ids.includes(id)) return ids.filter((x) => x !== id);
  if (kindOf(id, vocab) === 'stage') {
    return [...ids.filter((x) => kindOf(x, vocab) !== 'stage'), id];
  }
  return [...ids, id];
}

/**
 * The name a customer reads for a chosen tag, even one the vocabulary no longer lists:
 * its CRM tag name, else the name it had when it was withdrawn, else "Unavailable tag".
 * A raw internal key is never shown.
 */
export function labelFor(id: string, vocab: CrmTagVocabulary): string {
  return vocab.tags.find((t) => t.id === id)?.label
    ?? vocab.retired?.find((t) => t.id === id)?.label
    ?? UNAVAILABLE_TAG;
}

/**
 * The chosen tags that are no longer available. Saving any of them would be refused, so
 * every editor shows them as withdrawn and blocks its save until each is removed.
 * Empty while the vocabulary is still loading.
 */
export function withdrawnIds(ids: readonly string[], vocab: CrmTagVocabulary): string[] {
  if (vocab.pending) return [];
  return ids.filter((id) => !vocab.tags.some((t) => t.id === id));
}

/** Why a save is blocked by withdrawn tags, in one plain sentence. Null when it is not. */
export function withdrawnNote(ids: readonly string[], vocab: CrmTagVocabulary): string | null {
  const gone = withdrawnIds(ids, vocab);
  if (gone.length === 0) return null;
  const names = gone.map((id) => `“${labelFor(id, vocab)}”`).join(', ');
  return gone.length === 1
    ? `${names} is no longer available in your CRM tags. Remove it, or choose another tag, to continue.`
    : `${names} are no longer available in your CRM tags. Remove them, or choose other tags, to continue.`;
}

/**
 * A Review row's tags, by name, stage first: each `{ kind, key }` resolved through the
 * vocabulary, then the withdrawn list. Empty while the vocabulary is loading, rather than
 * a row of "Unavailable tag" that corrects itself a moment later.
 */
export function rowTagLabels(
  tags: ReadonlyArray<{ kind: string; key: string }> | undefined, vocab: CrmTagVocabulary | undefined,
): string[] {
  if (!tags || !vocab || vocab.pending) return [];
  return tags.map((t) => labelFor(`${t.kind}:${t.key}`, vocab));
}

/** Whether a chosen tag would actually reach the CRM. */
export const reachesCrm = (id: string, vocab: CrmTagVocabulary): boolean =>
  vocab.tags.some((t) => t.id === id);

/** A stage key's CRM tag name, or null when the stage has none. */
export function stageLabel(key: string | null, vocab: CrmTagVocabulary | undefined): string | null {
  if (!key || !vocab) return null;
  return vocab.tags.find((t) => t.kind === 'stage' && t.key === key)?.label ?? null;
}

/** "Only one of Signal - Prospect, Signal - Lead, Signal - Existing Customer can be on a contact." */
export function stageNote(vocab: CrmTagVocabulary, kinds: readonly CrmTagKind[] = ALL_KINDS): string | null {
  const stages = pickerOptions(vocab, kinds).filter((t) => t.kind === 'stage');
  if (stages.length < 2) return null;
  return `Only one of ${stages.map((t) => t.label).join(', ')} can be on a contact.`;
}

/** Same members, any order. */
export const sameSelection = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((x) => b.includes(x));
